import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { ChevronLeft, Loader2 } from "lucide-react";
import { AiReviewPanel, AiRowsTable, HeldImportPanel, DuplicateWarning } from "@/components/AiReviewPanel";

/**
 * Statement import detail — shows the extracted transactions from one
 * bank-statement upload. Every row is already promoted to `transactions`
 * (auto-promote flow), so this view is read-only + green "promoted →" pills.
 */
export default function StatementImportDetail() {
  const { importId } = useParams();
  const { currentId } = useCompany();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!currentId || !importId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/companies/${currentId}/statements/imports/${importId}`);
        if (!cancelled) setDoc(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentId, importId, reloadKey]);
  // AI Review imports still processing — poll until they settle.
  useEffect(() => {
    if (doc?.method !== "ai_review" || doc?.status !== "processing") return;
    const t = setTimeout(() => setReloadKey(k => k + 1), 4000);
    return () => clearTimeout(t);
  }, [doc]);

  if (loading) {
    return (
      <div className="p-8 text-slate-500 flex items-center gap-2">
        <Loader2 className="animate-spin" size={16} />Loading import…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-red-700 bg-red-50 border border-red-200 rounded-lg">
        {error}
      </div>
    );
  }
  if (!doc) return null;

  // Net change in the statement balance (ending − beginning); falls back
  // to the sum of posted amounts when the summary balances are unknown.
  const _bal = doc.ai_review?.summary || doc.veryfi_raw || {};
  const _beg = _bal.beginning_balance ?? _bal.starting_balance;
  const _end = _bal.ending_balance;
  const net = (_beg != null && _end != null)
    ? Number(_end) - Number(_beg)
    : (doc.transactions || []).reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const veryfi = doc.veryfi_raw || {};

  return (
    <div className="space-y-4" data-testid="stmt-detail-page">
      <Link
        to={`/connections`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
      >
        <ChevronLeft size={14} />Imports
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {doc.filename}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {doc.status === "needs_review"
              ? <>0 posted · {(doc.ai_rows || []).length} extracted</>
              : <>{(doc.transactions || []).length} transactions</>}
            {doc.status !== "needs_review" && (doc.transactions || []).length > 0 && (
              <span data-testid="stmt-detail-net"> · net {net < 0 ? "−" : ""}${Math.abs(net).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            )}
          </p>
        </div>
        <span data-testid="stmt-detail-status" className={"inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full " +
          (doc.status === "completed"
            ? "bg-emerald-100 text-emerald-800"
            : doc.status === "failed"
              ? "bg-red-100 text-red-800"
              : doc.status === "needs_review"
                ? "bg-rose-100 text-rose-800"
                : "bg-amber-100 text-amber-800")}>
          {doc.status === "needs_review" ? "needs review" : doc.status}
          {doc.method === "ai_review" && doc.status === "processing" && doc.ai_stage ? ` · ${doc.ai_stage}` : ""}
        </span>
      </div>

      {doc.method === "ai_review" && doc.ai_review && (
        <AiReviewPanel doc={doc} companyId={currentId} onChanged={() => setReloadKey(k => k + 1)} />
      )}
      {doc.method !== "ai_review" && doc.status === "needs_review" && (
        <HeldImportPanel doc={doc} companyId={currentId} onChanged={() => setReloadKey(k => k + 1)} />
      )}
      {doc.method !== "ai_review" && doc.status !== "needs_review" && doc.duplicate_warning && <DuplicateWarning dup={doc.duplicate_warning} />}
      {doc.status === "needs_review" && <AiRowsTable rows={doc.ai_rows} />}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <MetaCard label="METHOD" value={doc.method === "ai_review" ? "AI Review · GPT-4o" : `${doc.method || "—"} · bank_statement`} />
        <MetaCard label="ACCOUNT" value={
          doc.account_code && doc.account_name
            ? `${doc.account_code} · ${doc.account_name}`
            : (doc.account_name || "—")
        } />
        <MetaCard label="PERIOD" value={
          doc.period_start && doc.period_end
            ? `${doc.period_start} → ${doc.period_end}`
            : "—"
        } />
        <MetaCard label="UPLOADED" value={
          doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"
        } />
      </div>

      {/* OCR sanity banner — surfaces when Veryfi's line items don't
          reconcile to its own summary totals for this PDF, or when
          Layers 1+2 dropped rows that looked like phantom sidebars /
          balance columns. Aug 23 2026. */}
      {doc.method !== "ai_review" && (doc.ocr_reconcile_flag || (doc.ocr_dropped_rows && Object.keys(doc.ocr_dropped_rows).length > 0)) && (
        <div
          className={
            "rounded-xl border p-4 " +
            (doc.ocr_reconcile_flag
              ? "border-rose-200 bg-rose-50/60"
              : "border-amber-200 bg-amber-50/60")
          }
          data-testid="ocr-sanity-banner"
        >
          <div className={
            "text-xs uppercase tracking-widest font-semibold mb-1 " +
            (doc.ocr_reconcile_flag ? "text-rose-800" : "text-amber-800")
          }>
            {doc.ocr_reconcile_flag
              ? "This statement doesn't reconcile — review before finalizing"
              : "OCR cleaned up phantom rows from this statement"}
          </div>
          <div className={
            "text-sm " + (doc.ocr_reconcile_flag ? "text-rose-900" : "text-amber-900")
          }>
            {doc.ocr_reconcile_flag && (
              <>
                The extracted line items don't add up to the statement's own summary
                totals{doc.ocr_reconcile_flag.is_liability ? " (credit-card math: beginning − payments + purchases)" : ""} — expected ending balance{" "}
                <span className="font-mono">${Number(doc.ocr_reconcile_flag.ending_balance).toFixed(2)}</span>,
                computed{" "}
                <span className="font-mono">${Number(doc.ocr_reconcile_flag.computed_ending).toFixed(2)}</span>
                {" "}(drift{" "}
                <span className="font-mono font-semibold">
                  ${Math.abs(Number(doc.ocr_reconcile_flag.drift)).toFixed(2)}
                </span>).
                Some rows below may be phantom duplicates or the OCR may have
                missed a real transaction. Compare against the PDF before
                relying on the numbers.
              </>
            )}
            {!doc.ocr_reconcile_flag && doc.ocr_dropped_rows && (
              <>
                We dropped{" "}
                {Object.entries(doc.ocr_dropped_rows)
                  .map(([reason, n]) => `${n} ${reason.replace(/_/g, " ")}`)
                  .join(", ")}
                {" "}row(s) — either "Recent Deposits" summary boxes, balance
                columns, or duplicates Veryfi emitted across a page break. The
                remaining rows reconciled cleanly to the statement's own totals.
              </>
            )}
          </div>
        </div>
      )}

      {veryfi && (veryfi.bank_name || veryfi.account_holder_name || veryfi.starting_balance != null) && (
        <div className="rounded-xl border bg-white p-5 space-y-3">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
            Bank statement details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">INSTITUTION</div>
              <div className="font-medium">{veryfi.bank_name || "—"}</div>
              {veryfi.bank_address && <div className="text-xs text-slate-500 mt-1">{veryfi.bank_address}</div>}
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">ACCOUNT HOLDER</div>
              <div className="font-medium">{veryfi.account_holder_name || "—"}</div>
              {veryfi.account_holder_address && (
                <div className="text-xs text-slate-500 mt-1">{veryfi.account_holder_address}</div>
              )}
              {(veryfi.account_number || doc.last4) && (
                <div className="text-xs text-slate-500 mt-1 font-mono-num">
                  Account: {veryfi.account_number || `···${doc.last4}`}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">BALANCES</div>
              {veryfi.starting_balance != null && (
                <Row label="Beginning" amount={veryfi.starting_balance} />
              )}
              {veryfi.ending_balance != null && (
                <Row label="Ending" amount={veryfi.ending_balance} />
              )}
              {veryfi.starting_balance != null && veryfi.ending_balance != null && (
                <Row label="Net change"
                     amount={Number(veryfi.ending_balance) - Number(veryfi.starting_balance)}
                     colored />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
          <div>
            <div className="font-medium text-sm">Extracted transactions</div>
            <div className="text-xs text-slate-500">All rows have been auto-promoted to the ledger.</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left border-b">
            <tr className="text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2 font-medium">Date</th>
              <th className="px-4 py-2 font-medium">Description</th>
              <th className="px-4 py-2 font-medium">Category</th>
              <th className="px-4 py-2 font-medium text-right">Amount</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {(doc.transactions || []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                No transactions extracted.
              </td></tr>
            )}
            {(doc.transactions || []).map(t => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 tabular-nums text-slate-700 whitespace-nowrap">{t.date}</td>
                <td className="px-4 py-2 text-slate-700 max-w-[440px]">
                  <div className="truncate" title={t.description}>{t.description}</div>
                  {t.contact_name && (
                    <div className="text-xs text-slate-500 truncate">↳ {t.contact_name}</div>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-600 text-xs">
                  {t.category_account_code && t.category_account_name
                    ? `${t.category_account_code} · ${t.category_account_name}`
                    : "—"}
                </td>
                <td className={"px-4 py-2 text-right tabular-nums font-mono-num " +
                  (Number(t.amount) >= 0 ? "text-emerald-700" : "text-slate-800")}>
                  ${Math.abs(Number(t.amount) || 0).toLocaleString(undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="px-4 py-2">
                  {t.posted ? (
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      promoted →
                    </span>
                  ) : t.needs_review ? (
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                      needs review
                    </span>
                  ) : (
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      pending
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="text-sm text-slate-900 font-medium break-words">{value}</div>
    </div>
  );
}

function Row({ label, amount, colored = false }) {
  const n = Number(amount) || 0;
  const color = colored
    ? (n >= 0 ? "text-emerald-700" : "text-red-700")
    : "text-slate-800";
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono-num ${color}`}>
        {n < 0 ? "-" : ""}${Math.abs(n).toLocaleString(undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
}
